import { css, keyframes, useTheme } from "styled-components";
import { mobileBreakPointCSS } from "../defaults/theme.js";
import { BackgroundColor, FontSizes, Spacing } from "../types/theme.js";
import { getColorVar } from "./getColorVar.js";

export const surfaceColorMap: Record<BackgroundColor, string> = {
  background: "#fff",
  surface1: "var(--swatch3)",
  surface2: "var(--swatch4)",
  surface3: "var(--swatch2)",
};


export const cssSpacing = (...args: (keyof Spacing | number | "auto")[]) => {
  return () => {
    const theme = useTheme();
    return args
      .map((x) =>
        typeof x === "number" || x === "auto" ? x : `${theme.spacing[x]}`
      )
      .join(" ");
  };
};

export const cssFontSize = (size: keyof FontSizes) => {
  return () => {
    const theme = useTheme();
    return theme.fontSizes[size];
  };
};

const cssBackgroundGradientKeyframesMobile = keyframes`
  0% {
    background-position: 50% 40%;
  }

  50% {
    background-position: 50% 60%;
  }

  100% {
    background-position: 50% 40%;
  }
`;
const cssBackgroundGradientKeyframes = keyframes`
  0% {
    background-position: 150% -50%;
  }
  10% {
    background-position: 100% -35%;
  }
  20% {
    background-position: 50% -25%;
  }
  30% {
    background-position: 25% 12%;
  }
  40% {
    background-position: 0 0;
  }
  50% {
    background-position: 25% 25%;
  }
  60% {
    background-position: 50% 100%;
  }
  70% {
    background-position: 100% 150%;
  }
  80% {
    background-position: 150% 100%;
  }
  86% {
    background-position: 150% 75%;
  }
  90% {
    background-position: 150% 0;
  }
  100% {
    background-position: 150% -50%;
  }
`;

export type CssBackgroundGradientProps = {
  $variant?: string;
};

export const cssBackgroundGradient = css<CssBackgroundGradientProps>`
  color: ${(props) => {
    const color = props.$variant || "primary";
    return getColorVar("on" + color[0].toUpperCase() + color.slice(1));
  }};

  background-color: ${(props) => getColorVar(props.$variant || "primary")};
  background: radial-gradient(
    circle,
    var(--${(props) => props.$variant || "primary"}-gradient-blob) 10%,
    ${(props) => getColorVar(props.$variant || "primary")} 75%
  );

  background-size: 200% 150%;
  animation: ${cssBackgroundGradientKeyframesMobile} 18s linear infinite;
  @media (min-width: ${mobileBreakPointCSS}) {
    animation: ${cssBackgroundGradientKeyframes} 18s linear infinite;
  }

  h1,
  h2,
  h3,
  h4,
  p {
    text-shadow: 0 2px 2px rgba(0, 0, 0, 0.1);
  }

  a,
  button {
    box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1);
  }
`;

export const cssTitleAfterBorder = css`
  content: "";
  display: block;
  width: 100%;
  height: 3px;
  margin: ${(props) => props.theme.spacing.m} 0;
  background-color: var(--swatch1);
`;

export const buttonReset = css`
  border: none;
  margin: 0;
  padding: 0;
  width: auto;
  overflow: visible;
  font: inherit;
  -webkit-font-smoothing: inherit;
  -moz-osx-font-smoothing: inherit;
  -webkit-appearance: none;
  background-color: transparent;
`;
